import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Get } from "libs/services";
import { ITEM } from "libs/config";
import { setLoading } from "./loader";
import { StoreItemResponse } from "src/common/types/response/storeItems";
import { POPULAR_ITEMS_RATING } from "src/common/libs/utils/constants";
import { processPaginatedItems } from "src/common/libs/utils/items";
import { reportLog } from "src/common/libs/utils/logs";

interface StoreItemsState {
  data?: StoreItemResponse;
  loading: boolean;
  page: { [key: string]: number };
}

export const getStoreItems = createAsyncThunk(
  "store/getStoreItems",
  async (
    {
      id,
      guest,
      params = { page: 1 },
      latitude,
      longitude,
    }: {
      id?: string;
      guest: boolean;
      params?: {
        page: number;
        category?: string;
      };
      latitude: string;
      longitude: string;
    },
    thunkAPI
  ) => {
    try {
      const query = { ...params };
      if (id) {
        query["store"] = id;
      }
      if (guest) {
        query["role"] = "guest";
      }
      query["latitude"] = latitude;
      query["longitude"] = longitude;
      const response = await Get<StoreItemResponse>(ITEM.get, query);
      if (response.data.status) {
        return { key: id, data: response.data, page: params.page };
      }
      return { key: id, data: null, page: params.page };
    } catch (error) {
      reportLog({
        error: error,
        location: "features/storeItems->getStoreItems",
        body: { id, guest, params, latitude, longitude },
      });
      return { key: id, data: null, page: params.page };
    } finally {
      thunkAPI.dispatch(setLoading(false));
    }
  }
);

export const getStorePopularItems = createAsyncThunk(
  "store/getStorePopularItems",
  async (
    {
      id,
      guest,
      params = { rating: POPULAR_ITEMS_RATING },
      latitude,
      longitude,
    }: {
      id?: string;
      guest: boolean;
      params?: object;
      latitude: string;
      longitude: string;
    },
    thunkAPI
  ) => {
    try {
      const query = { ...params };
      if (id) {
        query["store"] = id;
      }
      if (guest) {
        query["role"] = "guest";
      }
      query["latitude"] = latitude;
      query["longitude"] = longitude;
      const response = await Get<StoreItemResponse>(ITEM.get, query);
      if (response.data.status) {
        return response.data;
      }
      return null;
    } catch (error) {
      reportLog({
        error: error,
        location: "features/storeItems->getStorePopularItems",
        body: { id, guest, params, latitude, longitude },
      });
      return null;
    } finally {
    }
  }
);

const initialState: StoreItemsState = {
  data: null,
  loading: false,
  page: {},
};

export const storeItemSlice = createSlice({
  name: "storeItems",
  initialState,
  reducers: {
    clearData: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getStoreItems.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getStoreItems.fulfilled, (state, action) => {
      const { data, page } = action.payload || {};
      const filteredPayload = processPaginatedItems<any>(
        data,
        (state.data && state.data[action.payload.key]?.payload) || []
      );
      if (page === 1) {
        state.data = {
          ...state.data,
          [action.payload.key]: filteredPayload,
        };
      } else {
        state.data = {
          ...state.data,
          [action.payload.key]: {
            ...filteredPayload,
            payload: [
              ...((state.data && state.data[action.payload.key].payload) || []),
              ...filteredPayload.payload,
            ],
          },
        };
      }
      state.page = { [action.payload.key]: page };
      state.loading = false;
    });
    builder.addCase(getStoreItems.rejected, (state, action) => {
      state.loading = false;
    });
  },
});

export const { clearData } = storeItemSlice.actions;

export default storeItemSlice.reducer;
